import { devLog, devError } from '../utils/devLogger';
import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Spinner, Alert, Row, Col, Toast, ToastContainer } from 'react-bootstrap';
import { collection, getDocs, query, orderBy, doc, updateDoc, serverTimestamp, where, documentId } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import NotificationService from '../services/notificationService';

const SubmissionRequestReviewPanel = () => {
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [error, setError] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [reviewForm, setReviewForm] = useState({ status: 'submitted', comment: '' });
  const [missingLearners, setMissingLearners] = useState([]);
  const [reminderSending, setReminderSending] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [cohortMap, setCohortMap] = useState({});

  useEffect(() => {
    const loadRequests = async () => {
      try {
        setLoadingRequests(true);
        setError('');
        const requestsRef = collection(db, 'submissionRequests');
        let snapshot;
        try {
          snapshot = await getDocs(query(requestsRef, orderBy('createdAt', 'desc')));
        } catch (queryError) {
          devLog('Submission request order query failed, falling back:', queryError);
          snapshot = await getDocs(requestsRef);
        }
        const data = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: docSnap.data().createdAt?.toDate?.() || null,
          dueDate: docSnap.data().dueDate?.toDate?.() || null
        }));
        setRequests(data);
      } catch (loadError) {
        devError('Error loading submission requests:', loadError);
        setError('Failed to load submission requests.');
      } finally {
        setLoadingRequests(false);
      }
    };

    loadRequests();
  }, []);

  useEffect(() => {
    const loadCohorts = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'cohorts'));
        const map = {};
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          map[docSnap.id] = data.name || data.cohortName || docSnap.id;
        });
        setCohortMap(map);
      } catch (err) {
        devError('Error loading cohorts:', err);
      }
    };

    loadCohorts();
  }, []);

  const loadSubmissions = async (request) => {
    if (!request) return;
    try {
      setLoadingSubmissions(true);
      setError('');
      const submissionsRef = collection(db, 'submissionRequests', request.id, 'submissions');
      let snapshot;
      try {
        snapshot = await getDocs(query(submissionsRef, orderBy('submittedAt', 'desc')));
      } catch (queryError) {
        devLog('Submission order query failed, falling back:', queryError);
        snapshot = await getDocs(submissionsRef);
      }
      const data = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        learnerId: docSnap.data().learnerId || docSnap.data().userId || docSnap.data().submittedById || docSnap.id,
        status: docSnap.data().status || 'submitted',
        submittedAt: docSnap.data().submittedAt?.toDate?.() || null
      }));
      setSubmissions(data);
      await loadMissingLearners(request, data);
    } catch (loadError) {
      devError('Error loading submissions:', loadError);
      setError('Failed to load submissions.');
      setSubmissions([]);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const loadMissingLearners = async (request, submissionList) => {
    try {
      const cohortIds = Array.isArray(request.cohortIds) ? request.cohortIds.filter(Boolean) : [];
      if (!cohortIds.length) {
        setMissingLearners([]);
        return;
      }
      const cohortNameMap = {};
      for (let i = 0; i < cohortIds.length; i += 10) {
        const chunk = cohortIds.slice(i, i + 10);
        const cohortSnap = await getDocs(query(collection(db, 'cohorts'), where(documentId(), 'in', chunk)));
        cohortSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          cohortNameMap[docSnap.id] = data.name || data.cohortName || docSnap.id;
        });
      }
      const learners = [];
      const learnerRoles = ['learner', 'applicant', 'student', 'graduate', 'candidate'];
      for (let i = 0; i < cohortIds.length; i += 10) {
        const chunk = cohortIds.slice(i, i + 10);
        const learnersQuery = query(
          collection(db, 'users'),
          where('cohortId', 'in', chunk)
        );
        const snapshot = await getDocs(learnersQuery);
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (!learnerRoles.includes(data.role)) return;
          learners.push({
            id: docSnap.id,
            name: data.displayName || data.name || data.email || 'N/A',
            email: data.email || '',
            cohortId: data.cohortId || '',
            cohortName: cohortNameMap[data.cohortId] || ''
          });
        });
      }
      const submittedIds = new Set(
        submissionList.flatMap((item) => [
          item.learnerId,
          item.userId,
          item.submittedById,
          item.id
        ].filter(Boolean))
      );
      const missing = learners
        .filter((learner) => !submittedIds.has(learner.id))
        .map((learner) => ({
          ...learner,
          cohortName: learner.cohortName || cohortMap[learner.cohortId] || ''
        }));
      setMissingLearners(missing);
    } catch (err) {
      devError('Error loading missing learners:', err);
      setMissingLearners([]);
    }
  };

  const openReviewModal = (submission) => {
    setSelectedSubmission(submission);
    setReviewForm({
      status: submission.status || 'submitted',
      comment: submission.latestComment || ''
    });
    setShowReviewModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedSubmission || !selectedRequest) return;
    try {
      const submissionRef = doc(db, 'submissionRequests', selectedRequest.id, 'submissions', selectedSubmission.id);
      const payload = {
        status: reviewForm.status,
        latestComment: reviewForm.comment || '',
        updatedAt: serverTimestamp(),
        reviewHistory: [
          ...(selectedSubmission.reviewHistory || []),
          {
            status: reviewForm.status,
            comment: reviewForm.comment || '',
            reviewedAt: new Date()
          }
        ]
      };
      await updateDoc(submissionRef, payload);

      if (selectedSubmission.learnerId) {
        await NotificationService.notify(
          'submission_status',
          'Submission Updated',
          `Your submission for "${selectedRequest.title}" was marked as ${reviewForm.status}.`,
          selectedSubmission.learnerId,
          { category: 'submission_requests', additionalData: { requestId: selectedRequest.id } }
        );
      }

      setShowReviewModal(false);
      setSelectedSubmission(null);
      setReviewSuccess('Review saved.');
      await loadSubmissions(selectedRequest);
    } catch (updateError) {
      devError('Error updating submission status:', updateError);
      setError('Failed to update submission status.');
    }
  };

  const handleRequestResubmit = async (submission) => {
    if (!submission || !selectedRequest) return;
    const comment = window.prompt('Add a resubmission note (optional):', 'Please resubmit your file.');
    if (comment === null) return;
    try {
      setError('');
      const submissionRef = doc(db, 'submissionRequests', selectedRequest.id, 'submissions', submission.id);
      const payload = {
        status: 'revision_requested',
        latestComment: comment || '',
        updatedAt: serverTimestamp(),
        reviewHistory: [
          ...(submission.reviewHistory || []),
          {
            status: 'revision_requested',
            comment: comment || '',
            reviewedAt: new Date()
          }
        ]
      };
      await updateDoc(submissionRef, payload);

      if (submission.learnerId) {
        await NotificationService.notify(
          'submission_status',
          'Resubmission Requested',
          `Please resubmit "${selectedRequest.title}".`,
          submission.learnerId,
          { category: 'submission_requests', additionalData: { requestId: selectedRequest.id } }
        );
      }

      setReviewSuccess('Resubmission request sent.');
      await loadSubmissions(selectedRequest);
    } catch (updateError) {
      devError('Error requesting resubmission:', updateError);
      setError('Failed to request resubmission.');
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredSubmissions = submissions.filter((submission) => {
    if (!normalizedSearch) return true;
    const name = (submission.learnerName || '').toLowerCase();
    const email = (submission.learnerEmail || '').toLowerCase();
    return name.includes(normalizedSearch) || email.includes(normalizedSearch);
  });

  const handleSendMissingReminders = async () => {
    if (!selectedRequest || missingLearners.length === 0) return;
    try {
      setReminderSending(true);
      const functions = getFunctions();
      const sendCustomEmail = httpsCallable(functions, 'sendCustomEmail');
      const subject = `Reminder: ${selectedRequest.title} submission`;
      const dueDate = selectedRequest.dueDate
        ? new Date(selectedRequest.dueDate).toLocaleDateString()
        : 'N/A';
      const htmlContent = `
        <p>Hello {name},</p>
        <p>This is a reminder to submit: <strong>${selectedRequest.title}</strong>.</p>
        <p>Due date: <strong>${dueDate}</strong>.</p>
        <p>Please log in to the LMS to upload your submission.</p>
      `;
      const recipients = missingLearners.map((learner) => learner.email).filter(Boolean);
      await sendCustomEmail({ subject, htmlContent, textContent: '', recipients });
      setError('');
    } catch (err) {
      devError('Error sending reminders:', err);
      setError('Failed to send reminder emails.');
    } finally {
      setReminderSending(false);
    }
  };

  return (
    <Card className="shadow-sm border-0 mb-4">
      <ToastContainer position="top-end" className="p-3">
        <Toast show={Boolean(reviewSuccess)} onClose={() => setReviewSuccess('')} delay={3000} autohide bg="success">
          <Toast.Body className="text-white">{reviewSuccess}</Toast.Body>
        </Toast>
        <Toast show={Boolean(error)} onClose={() => setError('')} delay={4000} autohide bg="danger">
          <Toast.Body className="text-white">{error}</Toast.Body>
        </Toast>
      </ToastContainer>
      <Card.Header className="bg-white">
        <h5 className="mb-0">Submission Requests Review</h5>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger">{error}</Alert>
        )}
        <Row>
          <Col md={5} className="mb-3">
            <h6 className="mb-2">Requests</h6>
            {loadingRequests ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : requests.length === 0 ? (
              <Alert variant="light">No submission requests found.</Alert>
            ) : (
              <Table bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => (
                    <tr
                      key={request.id}
                      role="button"
                      onClick={() => {
                        setSelectedRequest(request);
                        loadSubmissions(request);
                      }}
                      className={selectedRequest?.id === request.id ? 'table-primary' : ''}
                    >
                      <td>{request.title}</td>
                      <td>{request.dueDate ? new Date(request.dueDate).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Col>
          <Col md={7}>
            <h6 className="mb-2">Submissions</h6>
            {!selectedRequest ? (
              <Alert variant="light">Select a request to view submissions.</Alert>
            ) : loadingSubmissions ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <Alert variant="light">No submissions yet.</Alert>
            ) : (
              <>
                <Form.Control
                  type="text"
                  placeholder="Search learner name or email"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mb-2"
                />
                <Table bordered hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>Submitted</th>
                      <th>Status</th>
                      <th>File</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubmissions.map((submission) => (
                      <tr key={submission.id}>
                        <td>
                          <div>{submission.learnerName || 'Learner'}</div>
                          <div className="small text-muted">{submission.learnerEmail || ''}</div>
                        </td>
                        <td>{submission.submittedAt ? submission.submittedAt.toLocaleString() : 'N/A'}</td>
                        <td>
                          <Badge bg={submission.status === 'approved' ? 'success' : submission.status === 'rejected' ? 'danger' : submission.status === 'revision_requested' ? 'warning' : 'info'}>
                            {submission.status || 'submitted'}
                          </Badge>
                        </td>
                        <td>
                          {submission.fileUrl ? (
                            <Button size="sm" variant="outline-primary" onClick={() => window.open(submission.fileUrl, '_blank')}>
                              View
                            </Button>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="d-flex flex-wrap gap-2">
                          <Button size="sm" variant="outline-warning" onClick={() => handleRequestResubmit(submission)}>
                            Request Resubmit
                          </Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => openReviewModal(submission)}>
                            Review
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
            {selectedRequest && (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Missing submissions: {missingLearners.length}</strong>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={missingLearners.length === 0 || reminderSending}
                    onClick={handleSendMissingReminders}
                  >
                    {reminderSending ? 'Sending...' : 'Send Reminders'}
                  </Button>
                </div>
                {missingLearners.length === 0 ? (
                  <Alert variant="light" className="mb-0">All learners have submitted.</Alert>
                ) : (
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Learner</th>
                        <th>Email</th>
                        <th>Cohort</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missingLearners.map((learner) => (
                        <tr key={learner.id}>
                          <td>{learner.name}</td>
                          <td>{learner.email}</td>
                          <td>{learner.cohortName || cohortMap[learner.cohortId] || learner.cohortId || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            )}
          </Col>
        </Row>
      </Card.Body>

      <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Review Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={reviewForm.status}
              onChange={(e) => setReviewForm(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="revision_requested">Revision Requested</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Reason/Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reviewForm.comment}
              onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="Leave a reason or note..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReviewModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateStatus}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default SubmissionRequestReviewPanel;
